/**
 * 節の名前（slug）。docs/spec/problem-format.md §6.1、lesson-format.md §5.5。
 * ブラウザでも使う（レッスン画面の見出しの id、ルートの section との照合）。
 */
import type { ExplainSection, Inline, LessonDoc } from './types.js';

/** Inline[] を平文にする（text と inlineCode の値をつなぐ。emphasis・strong・link は中身だけ。break は空白）。 */
export function inlinePlainText(nodes: readonly Inline[]): string {
  let s = '';
  for (const x of nodes) {
    if (x.type === 'text' || x.type === 'inlineCode') s += x.value;
    else if (x.type === 'break') s += ' ';
    else s += inlinePlainText(x.children);
  }
  return s;
}

/**
 * 見出しの文字列（または front matter の section）を slug にする:
 * NFC にし、前後の空白を除き、連続する空白を `-` 1 つに置き換える。
 * 例: `Int に入る範囲` → `Int-に入る範囲`。
 */
export function sectionSlug(text: string): string {
  return text.normalize('NFC').trim().replace(/\s+/g, '-');
}

/** 見出しの Inline[] の slug（重複の連番を付ける前）。 */
export function headingSlug(nodes: readonly Inline[]): string {
  return sectionSlug(inlinePlainText(nodes));
}

/**
 * 1 つのレッスンの中で slug を一意にする採番器。最初の出現はそのまま、2 回目から `-2`、`-3` を付ける。
 * 連番を付けたものも既存の slug と衝突しないように進める。
 */
export function createSlugger(): (base: string) => { slug: string; duplicate: boolean } {
  const used = new Set<string>();
  const counts = new Map<string, number>();
  return (base) => {
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    if (n === 1 && !used.has(base)) {
      used.add(base);
      return { slug: base, duplicate: false };
    }
    let k = Math.max(n, 2);
    while (used.has(`${base}-${k}`)) k += 1;
    const slug = `${base}-${k}`;
    used.add(slug);
    return { slug, duplicate: true };
  };
}

/** レッスンの説明の節の見出し（`##` と、その中の `###`）。指せる節の一覧（§6.1）。 */
export interface LessonSectionHeading {
  depth: 2 | 3;
  /** 連番を付ける前の slug（リンクの照合に使う）。 */
  base: string;
  /** LessonDoc 上の slug（画面の id は `sec-<slug>`）。 */
  slug: string;
  children: Inline[];
}

export function lessonSectionHeadings(doc: Pick<LessonDoc, 'explain'>): LessonSectionHeading[] {
  const out: LessonSectionHeading[] = [];
  const push = (sec: ExplainSection): void => {
    out.push({ depth: 2, base: headingSlug(sec.heading), slug: sec.slug, children: sec.heading });
    for (const b of sec.blocks) {
      if (b.type === 'heading') {
        out.push({ depth: 3, base: headingSlug(b.children), slug: b.slug, children: b.children });
      }
    }
  };
  for (const sec of doc.explain) push(sec);
  return out;
}

export type ResolveSectionResult =
  { ok: true; heading: LessonSectionHeading } | { ok: false; reason: 'missing' | 'ambiguous' };

/**
 * section（見出しの文字列か slug）をレッスンの節に解決する。比較は両方を sectionSlug にしてから行う。
 * 同じ slug の見出しが 2 つ以上あれば ambiguous（problem-format.md §6.1。PF22）。
 */
export function resolveLessonSection(
  doc: Pick<LessonDoc, 'explain'>,
  section: string,
): ResolveSectionResult {
  const target = sectionSlug(section);
  const hits = lessonSectionHeadings(doc).filter((h) => h.base === target);
  const first = hits[0];
  if (first === undefined) return { ok: false, reason: 'missing' };
  if (hits.length > 1) return { ok: false, reason: 'ambiguous' };
  return { ok: true, heading: first };
}
